import React, { useState, useRef, useEffect, useCallback } from "react"
import { useSecureRequest } from "../../../hooks/useSecureRequest"
import { useCompany } from "../../../contexts/CompanyContext"
import { useConfig } from "../../../contexts/ConfigContext"
import { useRegister } from "../../../contexts/RegisterContext"
import { Button, Input, message, Radio, Tooltip } from "antd"
import { AppstoreOutlined, InsertRowAboveOutlined, PlusOutlined } from "@ant-design/icons"

const { Search } = Input

interface HeaderElementsProps {
  onAddProduct?: () => void
}

function HeaderElements({ onAddProduct }: Readonly<HeaderElementsProps>) {
  const [loading, setLoading] = useState(false)
  const [searchValue, setSearchValue] = useState("")
  const { company } = useCompany()
  const { config } = useConfig()
  const { securePost } = useSecureRequest()
  const { register, updateRegister } = useRegister()
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null)

  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current)
      }
    }
  }, [])

  const handleItemSearch = useCallback(async (text: string) => {
    setLoading(true)
    const trimmedText = text.trim()
    const path = trimmedText ? '/inventory/item/search-items' : '/inventory/item/get-items'
    try {
      const itemResponse = await securePost(path, {
        company: company.current?.uid,
        search: trimmedText,
        module: config.module
      })
      if (itemResponse.status === 200 && Array.isArray(itemResponse.itemList)) {
        if (itemResponse.itemList.length > 0) {
          updateRegister({ registerList: itemResponse.itemList })
        } else {
          message.error("No se encontraron resultados")
        }
      }
    } catch (error) {
      console.error("Error searching items:", error)
    } finally {
      setLoading(false)
    }
  }, [company, config.module, securePost, updateRegister])

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value
    setSearchValue(val)

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current)
    }

    if (!val.trim()) {
      handleItemSearch("")
    } else {
      searchTimeoutRef.current = setTimeout(() => {
        handleItemSearch(val)
      }, 400)
    }
  }

  const handleSearch = (value: string) => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current)
    }
    handleItemSearch(value)
  }

  return (
    <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center", justifyContent: "flex-end" }}>
      <Radio.Group
        value={register.storageViewType}
        onChange={(e) => updateRegister({ storageViewType: e.target.value })}
        optionType="button"
        buttonStyle="solid"
        style={{ display: "inline-flex", flexShrink: 0 }}
      >
        <Tooltip title="Tabla">
          <Radio.Button value="TABLE">
            <InsertRowAboveOutlined />
          </Radio.Button>
        </Tooltip>
        <Tooltip title="Lista">
          <Radio.Button value="LIST">
            <AppstoreOutlined />
          </Radio.Button>
        </Tooltip>
      </Radio.Group>

      <Search
        placeholder="Buscar por término"
        allowClear
        value={searchValue}
        onChange={handleChange}
        onSearch={handleSearch}
        loading={loading}
        enterButton
        style={{ width: 210, minWidth: 150, flex: "1 1 150px" }}
      />

      {onAddProduct && (
        <Button type="primary" icon={<PlusOutlined />} onClick={onAddProduct} htmlType="button" style={{ flexShrink: 0 }}>
          Crear Producto
        </Button>
      )}
    </div>
  )
}

export default HeaderElements